import {
	ArrowRight,
	Bath,
	BedDouble,
	House,
	Info,
	MoreHorizontal,
	NotebookText,
} from 'lucide-react'
/**
 * Approve / Decline / Withdraw / Abandon — ONE decision console for
 * the individual and combined pages (Decisions v4, client 2026-07-20;
 * supersedes the v3 defaults recorded 2026-07-16..18).
 *
 * Approve/decline open the member console under an INTENT title ("Accept /
 * Decline This Application?" — the neutral "Update This Application" title
 * retired): property meta strip, members grouped in role sections rendered
 * with the DASHBOARD MemberChip (same legend + hover tooltip), and
 * PER-MEMBER pills, varying by the console's entry (`consoleVariant`):
 *  - UNDECIDED rows carry Accept | Pending | Decline (Pending = leave
 *    undecided this pass — the 'later' state made visible; amber, mock
 *    2026-07-20). COMBINED entry seeds every undecided row with the
 *    clicked intent; INDIVIDUAL entry seeds only the ENTRY member's row —
 *    the rest open at Pending. No report gate — rule 5 retired; deciding
 *    an unfinished member warns, never blocks.
 *  - A DECIDED member's recorded decision renders as the selected
 *    PAST-TENSE pill ("Declined ✓") with only the opposite action beside
 *    it (no Pending — there is no path back to undecided); flipping posts
 *    the change, re-clicking the recorded pill reverts.
 *  - The row menu holds Withdraw only ("Leave as-is" retired — the
 *    Pending pill is the explicit no-change state).
 * Selections are validated live with the SAME validateMemberTransition
 * matrix the server enforces; mixed outcomes save per member; the note is
 * optional. Invitation rows show the invited identity (avatar + email +
 * "Invited …, not joined"); defaults split by variant (client 2026-07-20):
 * COMBINED defaults to CANCEL, hides the per-invite Cancel|Keep pills and
 * says so in a banner after the note field; INDIVIDUAL defaults to KEEP
 * with the pills shown, so Keep stays reachable from a member's own
 * console. A kept invitee can still join and re-open the household.
 *
 * Withdraw/Abandon keep the plain text confirm as HOUSEHOLD conveniences —
 * they post every member id and the server applies the action where legal.
 */
import { useState } from 'react'
import { useFetcher } from 'react-router'
import { MemberChip } from '#app/components/application-detail-v2/vendor/MemberChip'
import { StudioIcon } from '#app/components/application-detail-v2/vendor/studio-icon'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import {
	type HouseholdInviteChoice,
	type MemberDecisionAction,
	deriveDecisionDialogPlan,
	deriveDefaultMemberChoice,
	summarizeHouseholdTally,
} from '#app/utils/application-detail-v2/derive'
import type {
	ApplicationSummary,
	DetailActionResult,
	GroupMember,
	MemberTimeline,
	PendingInvitationSummary,
	PropertySummary,
	ScreeningKind,
} from '#app/utils/application-detail-v2/types'
import {
	type DashboardMemberChipData,
	deriveShortMemberName,
	deriveSingularFactLabel,
} from '#app/utils/application-detail-v2/vendor/card-kit'
import { formatBedroomsShort } from '#app/utils/application-detail-v2/vendor/format-bedrooms'
import { createAvatarName } from '#app/utils/avatar-name'
import { isDecisionLocked } from '#app/utils/landlord-application-actions'
import { cn } from '#app/utils/misc'
import {
	ActionResultMarker,
	DetailDialog,
	InlineActionError,
	PillButton,
	type PillButtonTone,
} from './dialog-bits'

export type DecisionIntent = 'approve' | 'decline' | 'withdraw' | 'abandon'

interface DecisionCopy {
	title: string
	guarantorBody: string
	/** withdraw/abandon plain-confirm copy; approve/decline render the member
	 * sections instead (2026-07-16 redesign) and never read these. */
	householdBody?: (count: number) => string
	soloBody?: string
	noteLabel: string
	notePlaceholder: string
	cta: string
	busy: string
	tone: PillButtonTone
	toastFallback: string
	/** Non-null = approve/decline (interactive member sections); null = plain
	 * confirm, no list. Also the guarantor-entry static pill text. */
	memberPill: string | null
	memberPillClass: string
}

const statusBody = (status: 'Withdrawn' | 'Abandoned'): DecisionCopy => ({
	title: 'Change Application Status',
	guarantorBody: `Are you sure you want to change this application’s status to “${status}”?`,
	householdBody: (count) =>
		`This changes the entire household — all ${count} applicants will be set to “${status}”. Undecided guarantors follow the household decision.`,
	soloBody: `Are you sure you want to change this application’s status to “${status}”?`,
	noteLabel:
		status === 'Withdrawn'
			? 'Reason for withdrawal (optional)'
			: 'Reason for abandoning (optional)',
	notePlaceholder:
		status === 'Withdrawn'
			? 'e.g., Tenant found another property...'
			: 'e.g., No progress on application, steps incomplete...',
	cta: 'Confirm',
	busy: 'Updating...',
	tone: 'danger',
	toastFallback: 'Application status updated',
	memberPill: null,
	memberPillClass: '',
})

const DECISION_COPY: Record<DecisionIntent, DecisionCopy> = {
	approve: {
		title: 'Accept This Application?',
		guarantorBody:
			'Are you sure you want to accept this guarantor? Their status will be updated to “Accepted”.',
		noteLabel: 'Note (optional)',
		notePlaceholder: 'e.g., Great credit score, verified income...',
		cta: 'Accept application',
		busy: 'Accepting...',
		tone: 'success',
		toastFallback: 'Application accepted',
		memberPill: 'Will be accepted',
		memberPillClass: 'bg-[#E8F5EC] text-[#167A3D]',
	},
	decline: {
		title: 'Decline This Application?',
		guarantorBody:
			'Are you sure you want to decline this guarantor? Their status will be updated to “Declined”.',
		noteLabel: 'Reason for declining (optional)',
		notePlaceholder: 'e.g., Insufficient income, failed background check...',
		cta: 'Decline application',
		busy: 'Declining...',
		tone: 'danger',
		toastFallback: 'Application declined',
		memberPill: 'Will be declined',
		memberPillClass: 'bg-[#FBEAE5] text-[#C23A20]',
	},
	withdraw: statusBody('Withdrawn'),
	abandon: statusBody('Abandoned'),
}

/** Property meta strip under the title (redesign): address · beds · baths ·
 * unit. Spans only — it renders inside the DialogDescription <p>. */
function PropertyMetaStrip({ property }: { property: PropertySummary }) {
	// Third tuple slot flags the studio row → rendered as the studio glyph
	// (no bed icon for a studio); parity with the cards / property strip.
	const items: Array<[typeof House, string, boolean?]> = [
		[House, property.title],
	]
	const bedrooms = formatBedroomsShort(property.bedrooms)
	if (bedrooms) {
		items.push([
			BedDouble,
			deriveSingularFactLabel(bedrooms) ?? bedrooms,
			bedrooms.toLowerCase() === 'studio',
		])
	}
	// A studio (0 bedrooms) has no bathroom fact — the item is dropped.
	if (property.bathrooms !== null && property.bedrooms !== 0) {
		items.push([Bath, `${property.bathrooms} Bath`])
	}
	if (property.unitNumber) {
		items.push([NotebookText, `Unit ${property.unitNumber}`])
	}
	return (
		<span className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#32302F]">
			{items.map(([Icon, label, isStudio]) => (
				<span key={label} className="inline-flex items-center gap-1.5">
					{isStudio ? (
						<StudioIcon className="size-4 text-[#747474]" />
					) : (
						<Icon className="size-4 text-[#747474]" />
					)}
					{label}
				</span>
			))}
		</span>
	)
}

/** Pill tone → selected / unselected classes. 'pending' is the amber
 * leave-undecided pill (mock 2026-07-20); 'keep' is the neutral Keep
 * Invite pill (mock: dark text, no outcome color). */
const CHOICE_PILL_TONES = {
	accept: {
		selected: 'border-transparent bg-[#F0FDF4] text-[#16A34A]',
		idle: 'text-[#16A34A]',
	},
	pending: {
		selected: 'border-transparent bg-[#FFF6EA] text-[#F59F0A]',
		idle: 'text-[#F59F0A]',
	},
	decline: {
		selected: 'border-transparent bg-[#FDF4F5] text-[#DC2634]',
		idle: 'text-[#DC2634]',
	},
	keep: {
		selected: 'border-transparent bg-[#F4F4F2] text-[#32302F]',
		idle: 'text-[#32302F]',
	},
} as const

/** One radio-look choice pill (aria-pressed toggle button — the
 * PropertyStrip pattern; Biome's useSemanticElements rules out role=radio). */
function ChoicePill({
	label,
	memberName,
	tone,
	selected,
	disabled,
	onSelect,
}: {
	label: string
	memberName: string
	tone: keyof typeof CHOICE_PILL_TONES
	selected: boolean
	disabled: boolean
	onSelect: () => void
}) {
	return (
		<button
			type="button"
			aria-pressed={selected}
			aria-label={`${label} ${memberName}`}
			disabled={disabled}
			onClick={onSelect}
			className={cn(
				'flex cursor-pointer items-center gap-2 rounded-[15px] border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
				selected
					? CHOICE_PILL_TONES[tone].selected
					: cn(
							'border-[#F0F0F0] hover:border-[#D9D6D3]',
							CHOICE_PILL_TONES[tone].idle,
						),
			)}
		>
			<span
				className={cn(
					'flex size-4 items-center justify-center rounded-full border',
					selected ? 'border-[#1F1E1D]' : 'border-[#D9D6D3]',
				)}
			>
				{selected ? (
					<span className="size-2 rounded-full bg-[#1F1E1D]" />
				) : null}
			</span>
			{label}
		</button>
	)
}

const SECTION_CHIP: Record<'applicant' | 'guarantor', string> = {
	applicant: 'bg-[#F0FDF4] text-[#16A34A]',
	guarantor: 'bg-[#FFF6EA] text-[#F59F0A]',
}

/** Decided-state chip on a member row (v3: the row stays editable). */
const STATE_CHIP: Record<string, string> = {
	accepted: 'bg-[#F0FDF4] text-[#16A34A]',
	declined: 'bg-[#FDF4F5] text-[#DC2634]',
	withdrawn: 'bg-[#F4F4F2] text-[#747474]',
	abandoned: 'bg-[#F4F4F2] text-[#747474]',
}
const STATE_LABEL: Record<string, string> = {
	accepted: 'Accepted',
	declined: 'Declined',
	withdrawn: 'Withdrawn',
	abandoned: 'Abandoned',
}
const ACTION_LABEL: Record<MemberDecisionAction, string> = {
	accept: 'Accepted',
	decline: 'Declined',
	withdraw: 'Withdrawn',
	abandon: 'Abandoned',
}

/** Per-row overflow: Withdraw — the v4 action beyond the Accept|Decline
 * pills ("Leave as-is" retired, client 2026-07-20: deselecting a pill IS
 * leave-as-is; Abandon retired as an offered action, owner 2026-07-17). */
function RowActionMenu({
	memberName,
	disabled,
	onSelect,
}: {
	memberName: string
	disabled: boolean
	onSelect: (action: MemberDecisionAction) => void
}) {
	return (
		// modal={false}: a modal menu portaled out of the Dialog makes the
		// dismiss click cascade and close the whole dialog (Radix nesting bug).
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger
				disabled={disabled}
				aria-label={`More decisions for ${memberName}`}
				className="flex size-8 cursor-pointer items-center justify-center rounded-full border border-[#F0F0F0] text-[#747474] transition-colors hover:border-[#D9D6D3] disabled:cursor-not-allowed disabled:opacity-60"
			>
				<MoreHorizontal className="size-4" />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuItem onSelect={() => onSelect('withdraw')}>
					Withdraw
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

/** Invite row identity (approved mock 2026-07-17): avatar initial +
 * stacked email / "Invited …, not joined" caption — shared between the
 * console rows and the Decision Saved receipt. The caption now shows on
 * BOTH (client 2026-07-20 mock; retires the 2026-07-17 email-only console
 * row — with the combined console hiding the pills, the caption carries
 * the row's meaning). */
function InviteIdentity({
	name,
	role,
}: {
	name: string
	role: 'co_applicant' | 'guarantor'
}) {
	return (
		<span className="flex min-w-0 items-center gap-3">
			<span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-[#E2E2E0] bg-white text-[10px] font-medium text-[#32302F]">
				{createAvatarName(name)}
			</span>
			<span className="flex min-w-0 flex-col">
				{/* text-xs matches MemberChip's name so an invite row and a member
				    row in the same list read at one size (owner 2026-07-18).
				    A long email WRAPS to a second line (line-clamp-2 + break-all —
				    emails carry no spaces to break on) instead of truncating or
				    shoving the pills onto their own row (owner 2026-07-18). */}
				<span className="line-clamp-2 text-xs font-medium break-all text-[#1F1E1D]">
					{name}
				</span>
				<span className="text-xs text-[#747474]">
					Invited {role === 'guarantor' ? 'Guarantor' : 'Co-applicant'}, not
					joined
				</span>
			</span>
		</span>
	)
}

/** Neutral dark outcome chip on an invite row — ONE rendering shared by the
 * combined console (static "Invite Cancelled", the row's fixed outcome
 * there) and the Decision Saved receipt (Kept Open / Cancelled). Never a
 * red: a cancelled invite is bookkeeping, not a member decline (client
 * 2026-07-21). */
function InviteOutcomeChip({ label }: { label: string }) {
	return (
		<span className="inline-flex shrink-0 items-center rounded-full bg-[#F4F4F2] px-3.5 py-1.5 text-[13px] font-semibold text-[#32302F]">
			{label}
		</span>
	)
}

/** What the saved submit did, snapshotted at submit time (revalidation
 * changes the live rows underneath the receipt). */
interface SavedMemberRow {
	key: string
	chip: DashboardMemberChipData
	outcome: string
	outcomeClass: string
}
interface SavedDecisionView {
	sections: Array<{
		label: string
		plural: boolean
		chipTone: 'applicant' | 'guarantor'
		rows: SavedMemberRow[]
	}>
	invites: Array<{
		id: string
		name: string
		role: 'co_applicant' | 'guarantor'
		kept: boolean
	}>
	summary: string
}

/** Post-submit receipt (approved mock 2026-07-17): the SAME role sections,
 * member chips and invite rows as the console, each row carrying its saved
 * outcome chip — Accepted / Declined / Withdrawn / Abandoned in OUR
 * terminology (never "Approved"), Pending for untouched members, and
 * Invite Kept Open / Invite Cancelled for invitations. */
function DecisionSavedBody({ view }: { view: SavedDecisionView }) {
	return (
		<div className="mt-2 flex flex-col gap-4">
			{view.sections.map((section) => (
				<div
					key={section.label}
					className="relative rounded-2xl border border-[#F0F0F0] p-3 pt-4"
				>
					<span
						className={cn(
							'absolute -top-2.5 left-4 rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
							SECTION_CHIP[section.chipTone],
						)}
					>
						{section.plural ? `${section.label}s` : section.label}
					</span>
					<div className="flex flex-col gap-2">
						{section.rows.map((row) => (
							<div
								key={row.key}
								className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#FBFDFD] p-2"
							>
								<MemberChip member={row.chip} />
								<span
									className={cn(
										'inline-flex shrink-0 items-center rounded-full px-3.5 py-1.5 text-[13px] font-semibold',
										row.outcomeClass,
									)}
								>
									{row.outcome}
								</span>
							</div>
						))}
					</div>
				</div>
			))}
			{view.invites.length > 0 ? (
				<div className="rounded-2xl border border-[#F0F0F0] p-3">
					<div className="flex flex-col gap-2">
						{view.invites.map((invite) => (
							<div
								key={invite.id}
								// No flex-wrap (owner 2026-07-18): the email wraps to two
								// lines inside its own column and the outcome chip stays
								// on the same row instead of dropping below.
								className="flex items-center justify-between gap-3 rounded-xl bg-[#FBFDFD] p-2"
							>
								<InviteIdentity name={invite.name} role={invite.role} />
								<InviteOutcomeChip
									label={invite.kept ? 'Invite Kept Open' : 'Invite Cancelled'}
								/>
							</div>
						))}
					</div>
				</div>
			) : null}
		</div>
	)
}

export function DecisionDialog({
	intent,
	open,
	onOpenChange,
	application,
	groupMembers,
	pendingInvitations = [],
	property,
	memberTimelines = [],
	screeningKind,
	consoleVariant = 'individual',
}: {
	intent: DecisionIntent
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	groupMembers: GroupMember[]
	/** Live household invitations — approve/decline render them as invited
	 * identity rows; defaults + controls split by `consoleVariant`. */
	pendingInvitations?: PendingInvitationSummary[]
	/** Header meta strip (address · beds · baths · unit) on approve/decline. */
	property?: PropertySummary
	/** Chip-legend inputs (RAW status, hasStarted, guarantorRequested) — the
	 * exact fields the dashboard chips receive, so the member chips here carry
	 * the same ring/badge legend (never a forked look, QA 2026-07-15). */
	memberTimelines?: MemberTimeline[]
	/** ENTRY application's screening tier — the solo fallback row's tooltip
	 * tier pill (grouped rows carry their own via GroupMember.screeningKind). */
	screeningKind?: ScreeningKind
	/** Which page opened the console (client 2026-07-20) — 'combined' seeds
	 * EVERY undecided row with the clicked intent, defaults invites to
	 * CANCEL, hides the per-invite pills and shows the banner; 'individual'
	 * (default) seeds only the ENTRY row (others open at Pending) and
	 * defaults invites to KEEP with the pills shown. */
	consoleVariant?: 'individual' | 'combined'
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const [note, setNote] = useState('')
	// Per-row pill state, keyed by application/invitation id; absent = the
	// default (opening intent on every UNDECIDED member, 'cancel' for
	// invites — Decisions v4). 'later' = no change this pass (a deselected
	// row; the "Leave as-is" menu item is retired). Reset on open via the
	// documented render-time prop-tracking pattern (no useEffect).
	const [choices, setChoices] = useState<
		Record<string, MemberDecisionAction | 'later'>
	>({})
	const [inviteChoices, setInviteChoices] = useState<
		Record<string, HouseholdInviteChoice>
	>({})
	// Post-submit receipt (mock 2026-07-17): non-null after a successful
	// interactive submit — the dialog swaps to the Decision Saved view until
	// Done/close. Snapshotted at submit time via the ref (revalidation
	// refreshes the live rows underneath).
	const [saved, setSaved] = useState<SavedDecisionView | null>(null)
	const [wasOpen, setWasOpen] = useState(open)
	if (open !== wasOpen) {
		setWasOpen(open)
		if (open) {
			setChoices({})
			setInviteChoices({})
			setSaved(null)
		}
	}
	const copy = DECISION_COPY[intent]
	const busy = fetcher.state !== 'idle'
	const isCombined = consoleVariant === 'combined'
	// Decisions v4 (client 2026-07-20): the COMBINED console pre-selects the
	// opening intent on EVERY undecided member (guarantors included; no
	// report gate); the INDIVIDUAL console seeds only the ENTRY row — the
	// rest open at Pending. Decided rows open showing their recorded
	// decision instead — deriveDefaultMemberChoice.
	const applicants = groupMembers.filter(
		(member: GroupMember) => member.applicationType !== 'guarantor',
	)
	const householdCount = Math.max(1, applicants.length)
	// v3: every entry (guarantor included) opens the same member-scoped
	// console — the guarantor-entry special case retired with the sweep.
	const body =
		copy.memberPill !== null ? (
			property ? (
				<PropertyMetaStrip property={property} />
			) : undefined
		) : application.applicationType === 'guarantor' ? (
			copy.guarantorBody
		) : householdCount > 1 ? (
			copy.householdBody?.(householdCount)
		) : (
			copy.soloBody
		)

	// Member rows for approve/decline. Solo pages have no groupMembers — the
	// household is the application itself ("a household of one", §1).
	const showMembers = copy.memberPill !== null
	interface MemberRow {
		key: string
		name: string
		type: 'primary' | 'co_applicant' | 'guarantor'
		roleLabel: string
		memberScreeningKind: ScreeningKind | null
		steps: GroupMember['steps']
		/** Derived-status fallback for the chip when no timeline entry exists
		 * (terminal values are spelled the same raw and derived). */
		fallbackStatus: string
		terminalStatus: string | null
	}
	const entryRow: MemberRow = {
		key: application.id,
		name: application.applicantName,
		type: application.applicationType,
		roleLabel:
			application.applicationType === 'guarantor' ? 'Guarantor' : 'Primary',
		memberScreeningKind: screeningKind ?? null,
		steps: undefined,
		fallbackStatus: application.derivedStatus,
		terminalStatus: isDecisionLocked(application.derivedStatus)
			? application.derivedStatus
			: null,
	}
	const memberRows: MemberRow[] =
		groupMembers.length > 0
			? groupMembers.map((member) => ({
					key: member.applicationId,
					name: member.displayName,
					type: member.applicationType,
					roleLabel: member.relationshipLabel,
					memberScreeningKind: member.screeningKind,
					steps: member.steps,
					fallbackStatus: member.status,
					terminalStatus: isDecisionLocked(member.status)
						? member.status
						: null,
				}))
			: [entryRow]

	const choiceOf = (id: string): MemberDecisionAction | 'later' => {
		const stored = choices[id]
		if (stored) return stored
		const row = memberRows.find((r) => r.key === id)
		if (!row) return 'later'
		return deriveDefaultMemberChoice({
			seedAllUndecided: isCombined,
			isEntry: row.key === application.id,
			currentStatus: row.fallbackStatus,
			intent,
		})
	}

	const invitations = pendingInvitations

	// The DASHBOARD member chip (ring/badge legend + hover tooltip). RAW
	// status / hasStarted / startedAt / infoRequested come from
	// memberTimelines — the exact inputs deriveMemberChipVisual expects
	// (GroupMember.status is DERIVED, feeding it directly would fork the
	// legend).
	const timelineByApp = new Map(
		memberTimelines.map((timeline) => [timeline.applicationId, timeline]),
	)
	const memberChipData = (row: MemberRow): DashboardMemberChipData => {
		const live = timelineByApp.get(row.key)
		return {
			key: row.key,
			shortName: deriveShortMemberName(row.name),
			fullName: row.name,
			initials: createAvatarName(row.name),
			roleLabel: row.roleLabel,
			kind: row.type === 'guarantor' ? 'guarantor' : 'applicant',
			status: live?.status ?? row.fallbackStatus,
			hasStarted: live?.hasStarted ?? true,
			screeningType: row.memberScreeningKind,
			startedAt: live?.startedAt ?? null,
			infoRequested: live?.infoRequested ?? false,
			billingHeld: live?.billingHeld ?? false,
			steps: row.steps ?? [],
			phone: null,
		}
	}
	// Live guard — the SAME validateMemberTransition matrix the server runs,
	// so Submit can only ever post a combination the server will save; an
	// illegal selection surfaces the server's exact refusal reason.
	const plan = deriveDecisionDialogPlan(
		memberRows.map((row) => ({
			id: row.key,
			role:
				row.type === 'guarantor'
					? ('guarantor' as const)
					: ('applicant' as const),
			currentStatus: row.fallbackStatus,
			choice: choiceOf(row.key),
		})),
	)
	// Invite defaults split by variant (client 2026-07-20): COMBINED defaults
	// to CANCEL (the banner says submitting will cancel; the pills are
	// hidden there), INDIVIDUAL defaults to KEEP with the Cancel|Keep pills
	// shown. A kept invitee can still join, and joining re-opens the
	// household (case D2). The posted inviteChoices carry the resolved
	// default, so the server does exactly what the console showed.
	const inviteChoiceOf = (id: string): HouseholdInviteChoice =>
		inviteChoices[id] ?? (isCombined ? 'cancel' : 'keep')
	// Invite cancels are submittable changes on their own — no member decision
	// required. Defaults count: the banner promises submit cancels them.
	const cancelCount = invitations.filter(
		(invite) => inviteChoiceOf(invite.id) === 'cancel',
	).length
	// Footer tally (client 2026-07-21): EVERY member's resulting state —
	// "0 Accepted · 1 Pending · 2 Declined" — not just the rows being
	// changed, so nobody reads a partial count as the whole household.
	// Cancelled invites are NOT in the tally — their outcome chip lives on
	// the invite rows only (repeating it below read as a duplicate).
	const tally = summarizeHouseholdTally(
		memberRows.map((row) => ({
			currentStatus: row.fallbackStatus,
			choice: choiceOf(row.key),
		})),
	)

	const roleSections = (
		[
			['primary', 'Primary', 'applicant'],
			['co_applicant', 'Co-Applicant', 'applicant'],
			['guarantor', 'Guarantor', 'guarantor'],
		] as const
	)
		.map(([type, label, chipTone]) => ({
			label,
			chipTone,
			rows: memberRows.filter((row) => row.type === type),
		}))
		.filter((section) => section.rows.length > 0)

	// The receipt's rows, resolved from the selections being posted. Untouched
	// rows show their recorded state, or "Pending" while still undecided.
	const ACTION_STATUS: Record<MemberDecisionAction, string> = {
		accept: 'accepted',
		decline: 'declined',
		withdraw: 'withdrawn',
		abandon: 'abandoned',
	}
	const buildSavedView = (): SavedDecisionView => ({
		sections: roleSections.map((section) => ({
			label: section.label,
			plural: section.rows.length > 1,
			chipTone: section.chipTone,
			rows: section.rows.map((row) => {
				const choice = choiceOf(row.key)
				const status =
					choice === 'later' ? row.terminalStatus : ACTION_STATUS[choice]
				const chip = memberChipData(row)
				return {
					key: row.key,
					// The snapshot is PRE-submit — stamp a changed row's chip with
					// the status being saved so the avatar's ring/badge agrees with
					// the outcome chip (terminal statuses spell the same raw/derived).
					chip:
						choice === 'later'
							? chip
							: { ...chip, status: ACTION_STATUS[choice] },
					outcome: status ? (STATE_LABEL[status] ?? status) : 'Pending',
					// Pending wears the SAME amber as the console's Pending pill
					// (CHOICE_PILL_TONES.pending) — the receipt must not invent a
					// second pending tone (client 2026-07-21).
					outcomeClass: status
						? (STATE_CHIP[status] ?? 'bg-[#F4F4F2] text-[#747474]')
						: 'bg-[#FFF6EA] text-[#F59F0A]',
				}
			}),
		})),
		invites: invitations.map((invitation) => ({
			id: invitation.id,
			name:
				invitation.invitedName?.trim() ||
				invitation.invitedEmail ||
				'Invited member',
			role: invitation.invitationType,
			kept: inviteChoiceOf(invitation.id) === 'keep',
		})),
		summary: tally,
	})

	const submitHousehold = () => {
		// Invite-only submits carry no member action — label the post with the
		// opening intent; the server takes the invite-only branch when
		// memberChoices is empty.
		if (!plan.postIntent && cancelCount === 0) return
		const memberChoices: Record<string, MemberDecisionAction> = {}
		for (const row of memberRows) {
			const choice = choiceOf(row.key)
			if (choice !== 'later') memberChoices[row.key] = choice
		}
		const form: Record<string, string> = {
			// Invite-only submits have no member action → label with the
			// opening intent; the server's invite-only branch handles it.
			intent: plan.postIntent ?? intent,
			applicationIds: application.id,
			memberChoices: JSON.stringify(memberChoices),
		}
		if (invitations.length > 0) {
			// Explicit cancels are honored even while the household stays open;
			// keeps are no-ops (invites die automatically when it closes).
			form.inviteChoices = JSON.stringify(
				Object.fromEntries(
					invitations.map((invite) => [invite.id, inviteChoiceOf(invite.id)]),
				),
			)
		}
		if (note.trim()) form.statusNote = note.trim()
		void fetcher.submit(form, { method: 'post' })
	}

	// Withdraw/Abandon plain confirms stay household-wide conveniences (v3
	// §7.1): post EVERY member id — the server applies the action where legal
	// and skips the rest (decided members need the note to change).
	const submitPlain = () => {
		const form: Record<string, string> = {
			intent,
			applicationIds: memberRows.map((row) => row.key).join(','),
		}
		if (note.trim()) form.statusNote = note.trim()
		void fetcher.submit(form, { method: 'post' })
	}
	// Note is OPTIONAL even on decision changes (owner 2026-07-18, reverses
	// v3 §7.4) — it never gates Submit; provided notes still land in the
	// member timeline via the audit reason.
	const submitBlocked =
		busy ||
		(!plan.postIntent && cancelCount === 0) ||
		plan.blockedReason !== null

	const interactive = showMembers

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback={copy.toastFallback}
				onSuccess={() => {
					setNote('')
					setSaved(null)
					onOpenChange(false)
				}}
			/>
			<DetailDialog
				open={open}
				onOpenChange={(next) => {
					onOpenChange(next)
					if (!next) setNote('')
				}}
				title={
					// The title names the CLICKED intent — "Accept / Decline This
					// Application?" (client 2026-07-20; retires the neutral "Update
					// This Application" of 2026-07-16). The per-row pills can still
					// mix outcomes; the receipt keeps its own title.
					saved ? 'Decision Saved' : copy.title
				}
				description={body}
				className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl"
			>
				{saved ? (
					<>
						<div className="flex min-h-0 flex-1 [scrollbar-width:thin] flex-col gap-5 overflow-y-auto pt-3">
							<DecisionSavedBody view={saved} />
							<InlineActionError fetcher={fetcher} open={open} />
						</div>
						<div className="-mx-7 -mb-7 flex shrink-0 items-center justify-between gap-3 border-t border-[#F0F0F0] bg-[#fbfdfd] px-7 py-4 sm:-mx-8 sm:-mb-8 sm:px-8">
							<p className="text-sm font-medium text-[#32302F]">
								{saved.summary}
							</p>
							<div className="flex items-center gap-3">
								<PillButton
									tone="outline"
									disabled={busy}
									onClick={() => setSaved(null)}
								>
									Back
								</PillButton>
								<PillButton
									tone="dark"
									disabled={busy}
									onClick={submitHousehold}
								>
									{busy ? 'Saving...' : 'Done'}
								</PillButton>
							</div>
						</div>
					</>
				) : (
					<>
						<div className="flex min-h-0 flex-1 [scrollbar-width:thin] flex-col gap-5 overflow-y-auto pt-3">
							{interactive ? (
								<div className="mt-2 flex flex-col gap-4">
									{roleSections.map((section) => (
										<div
											key={section.label}
											className="relative rounded-2xl border border-[#F0F0F0] p-3 pt-4"
										>
											<span
												className={cn(
													'absolute -top-2.5 left-4 rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
													SECTION_CHIP[section.chipTone],
												)}
											>
												{section.rows.length > 1
													? `${section.label}s`
													: section.label}
											</span>
											<div className="flex flex-col gap-2">
												{section.rows.map((row) => {
													const choice = choiceOf(row.key)
													// The recorded decision, when it maps onto a pill —
													// that pill renders PAST TENSE and shows selected
													// while the row is unchanged (client 2026-07-20;
													// reverses the 2026-07-17 hidden same-state pill).
													// Decided rows carry NO Pending pill: there is no
													// path back to undecided.
													const recorded =
														row.terminalStatus === 'accepted'
															? ('accept' as const)
															: row.terminalStatus === 'declined'
																? ('decline' as const)
																: null
													// Undecided rows read as a 3-way radio (Accept |
													// Pending | Decline — Pending is the visible
													// 'later', mock 2026-07-20). Clicking a selected
													// action pill (or a decided row's recorded pill)
													// resolves to 'later': back to Pending / the
													// recorded state.
													const pick = (
														action: MemberDecisionAction | 'later',
													) =>
														setChoices((prev) => ({
															...prev,
															[row.key]:
																action !== 'later' &&
																(choice === action || action === recorded)
																	? 'later'
																	: action,
														}))
													const pillSelected = (action: 'accept' | 'decline') =>
														choice === action ||
														(choice === 'later' && recorded === action)
													return (
														<div
															key={row.key}
															className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#FBFDFD] p-2"
														>
															<MemberChip member={memberChipData(row)} />
															<span className="flex shrink-0 items-center gap-2">
																{choice === 'withdraw' ||
																choice === 'abandon' ? (
																	<span className="inline-flex items-center rounded-full bg-[#F4F4F2] px-3 py-1 text-xs font-semibold text-[#32302F]">
																		→ {ACTION_LABEL[choice]}
																	</span>
																) : null}
																<ChoicePill
																	label={
																		recorded === 'accept'
																			? 'Accepted'
																			: 'Accept'
																	}
																	memberName={row.name}
																	tone="accept"
																	selected={pillSelected('accept')}
																	disabled={busy}
																	onSelect={() => pick('accept')}
																/>
																{recorded === null ? (
																	<ChoicePill
																		label="Pending"
																		memberName={row.name}
																		tone="pending"
																		selected={choice === 'later'}
																		disabled={busy}
																		onSelect={() => pick('later')}
																	/>
																) : null}
																<ChoicePill
																	label={
																		recorded === 'decline'
																			? 'Declined'
																			: 'Decline'
																	}
																	memberName={row.name}
																	tone="decline"
																	selected={pillSelected('decline')}
																	disabled={busy}
																	onSelect={() => pick('decline')}
																/>
																<RowActionMenu
																	memberName={row.name}
																	disabled={busy}
																	onSelect={(action) =>
																		setChoices((prev) => ({
																			...prev,
																			[row.key]: action,
																		}))
																	}
																/>
															</span>
														</div>
													)
												})}
											</div>
										</div>
									))}
									{invitations.length > 0 ? (
										<div className="rounded-2xl border border-[#F0F0F0] p-3">
											<div className="flex flex-col gap-2">
												{invitations.map((invitation) => {
													const invitedName =
														invitation.invitedName?.trim() ||
														invitation.invitedEmail ||
														'Invited member'
													return (
														<div
															key={invitation.id}
															// No flex-wrap (owner 2026-07-18): a long email wraps
															// to two lines in its own column while Cancel|Keep
															// stay on the same row.
															className="flex items-center justify-between gap-3 rounded-xl bg-[#FBFDFD] p-2"
														>
															<InviteIdentity
																name={invitedName}
																role={invitation.invitationType}
															/>
															{/* Combined-entry consoles hide the pills (client
															    2026-07-20) — submitting always cancels there, so
															    the row carries the static "Invite Cancelled"
															    outcome chip (client 2026-07-21; same chip as the
															    receipt) with the banner explaining why. The
															    individual console keeps the pills (default KEEP),
															    so Keep stays reachable there. */}
															{isCombined ? (
																<InviteOutcomeChip label="Invite Cancelled" />
															) : (
																<span className="flex shrink-0 items-center gap-2">
																	<ChoicePill
																		label="Cancel Invite"
																		memberName={invitedName}
																		tone="decline"
																		selected={
																			inviteChoiceOf(invitation.id) === 'cancel'
																		}
																		disabled={busy}
																		onSelect={() =>
																			setInviteChoices((prev) => ({
																				...prev,
																				[invitation.id]: 'cancel',
																			}))
																		}
																	/>
																	<ChoicePill
																		label="Keep Invite"
																		memberName={invitedName}
																		tone="keep"
																		selected={
																			inviteChoiceOf(invitation.id) === 'keep'
																		}
																		disabled={busy}
																		onSelect={() =>
																			setInviteChoices((prev) => ({
																				...prev,
																				[invitation.id]: 'keep',
																			}))
																		}
																	/>
																</span>
															)}
														</div>
													)
												})}
											</div>
										</div>
									) : null}
								</div>
							) : null}

							{/* The note input is removed from the approve/decline console
							    (client 2026-07-20); the withdraw/abandon plain confirms
							    keep their "Reason for withdrawal/abandoning" field. */}
							{interactive ? null : (
								<div className="flex flex-col gap-2">
									<label
										htmlFor={`decision-note-${intent}`}
										className="text-sm font-medium text-[#32302F]"
									>
										{copy.noteLabel}
									</label>
									<textarea
										id={`decision-note-${intent}`}
										rows={3}
										value={note}
										onChange={(event) => setNote(event.target.value)}
										disabled={busy}
										placeholder={copy.notePlaceholder}
										className="w-full resize-none rounded-xl border border-[#F0F0F0] px-4 py-3 text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC] focus:border-[#32302F]"
									/>
								</div>
							)}

							{/* The mock's banner (client 2026-07-20) — COMBINED console
							    only, at the BOTTOM after the note: invites default to
							    Cancel there, so say what Submit will do. The individual
							    console defaults to Keep with visible pills — the banner
							    would be untrue, so it doesn't render. */}
							{interactive && isCombined && invitations.length > 0 ? (
								<p className="flex items-start gap-2 rounded-xl bg-[#F4F4F2] px-4 py-3 text-[13px] leading-relaxed text-[#32302F]">
									<Info
										className="mt-0.5 size-4 shrink-0 text-[#747474]"
										aria-hidden="true"
									/>
									{/* No "switch to Keep" instruction (client 2026-07-21): the
									    combined console always cancels — there is no Keep pill
									    here to switch to. */}
									{invitations.length === 1
										? 'This household has 1 pending invitation. Submitting this decision will cancel the invitation, and the invitee will no longer be able to join the application.'
										: `This household has ${invitations.length} pending invitations. Submitting this decision will cancel the invitations, and the invitees will no longer be able to join the application.`}
								</p>
							) : null}

							{interactive && plan.blockedReason ? (
								<p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
									⚠ {plan.blockedReason}
								</p>
							) : null}

							<InlineActionError fetcher={fetcher} open={open} />
						</div>

						{interactive ? (
							<div className="-mx-7 -mb-7 flex shrink-0 items-center justify-between gap-3 border-t border-[#F0F0F0] bg-[#fbfdfd] px-7 py-4 sm:-mx-8 sm:-mb-8 sm:px-8">
								<p className="text-sm font-medium text-[#32302F]">{tally}</p>
								<PillButton
									tone="dark"
									disabled={submitBlocked}
									onClick={() => setSaved(buildSavedView())}
								>
									Submit
									<ArrowRight className="size-4" />
								</PillButton>
							</div>
						) : (
							<div className="flex shrink-0 justify-end gap-3">
								<PillButton
									tone="outline"
									onClick={() => onOpenChange(false)}
									disabled={busy}
								>
									Cancel
								</PillButton>
								<PillButton tone={'dark'} disabled={busy} onClick={submitPlain}>
									{busy ? copy.busy : copy.cta}
									<ArrowRight className="size-4" />
								</PillButton>
							</div>
						)}
					</>
				)}
			</DetailDialog>
		</>
	)
}
